import { block, text } from "../../arbre/print/template/registry";
import type {
  PrintFieldDef,
  PrintSectionDef,
} from "../../arbre/print/template/types";
import { formatValueWithSujetLabel } from "../../shared/utils/sujet-label";
import type { EpaysagePrintData } from "../print-alt/PrintEpaysageAlt";

/**
 * Données d'une section de la fiche détaillée : la fiche entière, et pour les sections « essence » l'essence imprimée
 * (avec son rang). Les sections de la fiche reçoivent `essence: null`.
 */
export interface EpaysageCtx {
  data: EpaysagePrintData;
  essence: any | null;
  index: number;
}

/** `{n}` est remplacé par le numéro de l'essence (1, 2…) dans les titres et libellés des sections « essence ». */
export const ESSENCE_NUMBER_TOKEN = "{n}";

/**
 * Fiche de l'espace boisé (page d'impression, PDF et impression multiple) : une page de synthèse puis, pour chaque
 * essence, ses sections (identité, diagnostic visuel, travaux). Les clés sont stockées dans les modèles : ne jamais
 * les renommer ni les réutiliser.
 */
export const EPAYSAGE_SECTIONS: PrintSectionDef[] = [
  { id: "identification", title: "", defaultColumns: 4 },
  { id: "adresse", title: "", defaultColumns: 1 },
  {
    id: "caracteristiques",
    title: "Caractéristiques",
    pdfTitle: "Caractéristiques",
    defaultColumns: 3,
  },
  { id: "risque_age", title: "", defaultColumns: 2 },
  { id: "photos", title: "", defaultColumns: 1 },
  { id: "commentaire", title: "", defaultColumns: 1 },
  {
    id: "essence_identite",
    title: `Essence ${ESSENCE_NUMBER_TOKEN}`,
    pdfTitle: `Essence ${ESSENCE_NUMBER_TOKEN}`,
    defaultColumns: 2,
    scope: "essence",
  },
  {
    id: "essence_diagnostic",
    title: "DIAGNOSTIC VISUEL",
    pdfTitle: "DIAGNOSTIC VISUEL",
    defaultColumns: 2,
    scope: "essence",
  },
  {
    id: "essence_travaux",
    title: "Travaux :",
    pdfTitle: "Travaux :",
    defaultColumns: 2,
    scope: "essence",
    plainTitle: true,
  },
];

const t = (
  key: string,
  section: string,
  label: string,
  col: number,
  resolve: PrintFieldDef<EpaysageCtx>["resolve"],
  extra: Partial<PrintFieldDef<EpaysageCtx>> = {},
) => text<EpaysageCtx>(key, section, label, col, resolve, extra);

const b = (
  key: string,
  section: string,
  label: string,
  extra: Partial<PrintFieldDef<EpaysageCtx>> = {},
) => block<EpaysageCtx>(key, section, label, extra);

const essenceName = (c: EpaysageCtx) =>
  c.data.essences[c.index] || `Essence ${c.index + 1}`;

/** Un travail n'est imprimé que s'il est renseigné (comme dans les fiches actuelles). */
const travail = (key: string, label: string, field: string) =>
  t(
    key,
    "essence_travaux",
    label,
    1,
    (c) => {
      const v = c.essence?.[field];
      return v === null || v === undefined || v === "" || v === "null"
        ? null
        : formatValueWithSujetLabel(v);
    },
    { hideIfEmpty: true },
  );

/** Même rendu que `formatDate` de @repo/ui (jj/mm/aaaa), sans dépendre de ce paquet. */
const formatDate = (value: string) =>
  new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(value));

const dateLe = (v?: string) => (v ? `Le ${formatDate(v)}` : null);

export const EPAYSAGE_FIELDS: PrintFieldDef<EpaysageCtx>[] = [
  // Identification (4 colonnes)
  t("date", "identification", "Date", 1, (c) => c.data.formattedDate, {
    locked: true,
  }),
  t("type_espace", "identification", "Type", 1, (c) => c.data.type_espace),
  t(
    "nb_arbres_total",
    "identification",
    "Nbr d'Arbres Total",
    1,
    (c) => c.data.totalSubjects,
  ),
  t("code_site", "identification", "Code", 2, (c) => c.data.code_site, {
    locked: true,
  }),
  t(
    "identification",
    "identification",
    "Identification",
    2,
    (c) => c.data.num_sujet,
    { locked: true },
  ),
  t("essence_1", "identification", "Essence 1", 3, (c) => c.data.essences[0]),
  t("essence_2", "identification", "Essence 2", 3, (c) => c.data.essences[1], {
    hideIfEmpty: true,
  }),
  t("essence_3", "identification", "Essence 3", 3, (c) => c.data.essences[2], {
    hideIfEmpty: true,
  }),
  t("essence_4", "identification", "Essence 4", 4, (c) => c.data.essences[3], {
    hideIfEmpty: true,
  }),
  t("essence_5", "identification", "Essence 5", 4, (c) => c.data.essences[4], {
    hideIfEmpty: true,
  }),
  t("surface", "identification", "Surface Totale", 4, (c) => c.data.surface),

  t("adresse", "adresse", "Adresse", 1, (c) => c.data.addressLine),

  // Caractéristiques
  t(
    "milieu",
    "caracteristiques",
    "Milieu",
    1,
    (c) => c.data.caracteristiques.milieu,
  ),
  t("sol", "caracteristiques", "Sol", 1, (c) => c.data.caracteristiques.sol),
  t(
    "nuisance",
    "caracteristiques",
    "Nuisance",
    1,
    (c) => c.data.environnement.nuisance,
  ),
  t(
    "situation",
    "caracteristiques",
    "Situation",
    2,
    (c) => c.data.environnement.situation,
  ),
  t(
    "domaine",
    "caracteristiques",
    "Domaine",
    2,
    (c) => c.data.caracteristiques.domaine,
  ),
  t(
    "proximite",
    "caracteristiques",
    "Cohabitation",
    2,
    (c) => c.data.caracteristiques.proximite,
  ),
  t(
    "accessibilite",
    "caracteristiques",
    "Accès",
    3,
    (c) => c.data.caracteristiques.accessibilite,
  ),
  t(
    "taux_freq",
    "caracteristiques",
    "Fréquentation",
    3,
    (c) => c.data.caracteristiques.taux_freq,
  ),
  t(
    "type_passage",
    "caracteristiques",
    "Type de Passage",
    3,
    (c) => c.data.caracteristiques.type_passage,
  ),

  // Risque global et âge
  b("bloc_risque_global", "risque_age", "Risque global de l'espace boisé", {
    defaultCol: 1,
  }),
  t(
    "age_estime",
    "risque_age",
    "Age moyen estimé du boisement",
    2,
    (c) => c.data.caracteristiques.age_estime,
  ),

  b("bloc_photos", "photos", "Photos"),
  b("bloc_commentaire", "commentaire", "Commentaire général de l'Espace Boisé"),

  // Sections « essence » (répétées pour chaque essence)
  t(
    "essence_nom",
    "essence_identite",
    `Essence ${ESSENCE_NUMBER_TOKEN}`,
    1,
    essenceName,
  ),
  t("essence_nb_arbres", "essence_identite", "Nbr d'arbres", 1, (c) =>
    c.essence?.count_subject
      ? formatValueWithSujetLabel(c.essence.count_subject)
      : null,
  ),
  t(
    "essence_diametre",
    "essence_identite",
    "Diamètre moyen",
    2,
    (c) => c.essence?.diametre,
  ),
  t(
    "essence_hauteur",
    "essence_identite",
    "Hauteur moyenne",
    2,
    (c) => c.essence?.hauteur,
  ),
  b("bloc_essence_photos", "essence_identite", "Photos de l'essence", {
    defaultCol: 1,
  }),

  t(
    "essence_stade_dev",
    "essence_diagnostic",
    "Stade de développement",
    1,
    (c) => c.essence?.stade_dev,
  ),
  t(
    "essence_etat",
    "essence_diagnostic",
    "État physiologique",
    1,
    (c) => c.essence?.etat_san_general,
  ),
  t(
    "essence_port",
    "essence_diagnostic",
    "Port général",
    2,
    (c) => c.essence?.port_arbre,
  ),
  t(
    "essence_critere",
    "essence_diagnostic",
    "Critères particuliers",
    2,
    (c) => c.essence?.critere,
  ),
  b(
    "bloc_essence_risque",
    "essence_diagnostic",
    `Risque de rupture générale essence ${ESSENCE_NUMBER_TOKEN}`,
    { defaultCol: 2 },
  ),

  travail("travaux_aucun", "- Aucun travaux", "travaux_aucun"),
  travail("travaux_abattage", "- Abattage", "travaux_abattage"),
  travail("travaux_demontage", "- Démontage", "travaux_demontage"),
  travail(
    "travaux_mise_en_securite",
    "- Mise en sécurité",
    "travaux_mise_en_securite",
  ),
  travail(
    "travaux_taille_sanitaire",
    "- Taille sanitaire",
    "travaux_taille_sanitaire",
  ),
  travail(
    "travaux_couple_selective",
    "- Couple sélective",
    "travaux_couple_selective",
  ),
  travail("travaux_dechiffrage", "- Défrichage", "travaux_dechiffrage"),
  travail("travaux_other", "- Préservation niche écologique", "travaux_other"),
  t(
    "date_travaux",
    "essence_travaux",
    "Date prévisionnelle des travaux",
    2,
    (c) => dateLe(c.essence?.user_edited_date_travaux),
  ),
  t("date_visite", "essence_travaux", "Date de la prochaine visite", 2, (c) =>
    dateLe(c.essence?.user_edited_date_pro_visite),
  ),
  t(
    "travaux_com",
    "essence_travaux",
    "Commentaires",
    2,
    (c) => c.essence?.travaux_com,
  ),
];
